// 경량 공유 캐시 — 여러 화면이 같은 URL을 호출할 때 '첫 진입 빈화면'을 제거(#1·#5)
// react-query 미사용 화면(SimScreen·HomeScreen)용 SWR-lite. 핵심 효과:
//   홈이 받아둔 /sim/portfolio 를 투자 탭이 다시 콜드 fetch하지 않고 즉시 재사용.
// TTL은 queryClient staleTime(30s)과 동기 — 신선도는 화면별 폴링/실시간 틱이 별도 보장.
import { apiFetch } from './api';

type Entry = { at: number; data: any };
const store = new Map<string, Entry>();        // url → 마지막 성공 응답
const inflight = new Map<string, Promise<any>>(); // url → 진행중 요청(중복 합류)
const TTL = 30_000;

// 동기 조회 — 화면 초기 state용(있으면 빈화면 스킵). TTL 지난 캐시는 null.
export function peekCache(url: string): any | null {
  const hit = store.get(url);
  return hit && Date.now() - hit.at < TTL ? hit.data : null;
}

// 캐시 우선 fetch — fresh 캐시 즉시반환 / 진행중이면 합류 / 아니면 fetch 후 저장.
// 실패는 throw(호출자 try/catch가 stale-while-revalidate 처리) — 에러는 캐시에 저장 안 함.
// force=true면 캐시·진행중을 무시하고 항상 새로 fetch(폴링용) — 결과는 캐시에 갱신해 다른 화면도 fresh.
export async function cachedFetch(url: string, opts?: any, force = false): Promise<any> {
  if (!force) {
    const hit = store.get(url);
    if (hit && Date.now() - hit.at < TTL) return hit.data;
    const flying = inflight.get(url);
    if (flying) return flying;
  }
  const p = apiFetch(url, opts)
    .then((data: any) => { store.set(url, { at: Date.now(), data }); return data; })
    .finally(() => { if (inflight.get(url) === p) inflight.delete(url); });
  if (!force) inflight.set(url, p);
  return p;
}

// fire-and-forget 프리페치(딥링크/사전 워밍용). 이미 캐시/진행중이면 noop. 실패 무시.
export function prefetch(url: string, opts?: any): void {
  if (peekCache(url) || inflight.has(url)) return;
  cachedFetch(url, opts).catch(() => { /* 프리페치 실패는 무시 — 화면이 재시도 */ });
}

// 강제 무효화 — 매도/충전 등 변경 직후 다음 진입을 fresh로 보장할 때(필요 시 호출).
export function invalidate(url?: string): void {
  if (url) { store.delete(url); inflight.delete(url); }
  else { store.clear(); inflight.clear(); }
}
